import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { authFetch } from "../../App";
import { format, startOfWeek, endOfWeek, addMonths, subMonths } from "date-fns";
import { de } from "date-fns/locale";
import {
  ArrowLeft, Calendar, ChevronLeft, ChevronRight, Clock, Loader2,
  TrendingUp, TrendingDown, Minus, Sun, Moon, Briefcase
} from "lucide-react";

const API = "/api";
const hm = (m) => { const v = Math.abs(m || 0); return `${Math.floor(v / 60)}:${(v % 60).toString().padStart(2, "0")}`; };

export default function MobileEmployeeStunden() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [viewMode, setViewMode] = useState("week"); // day, week, month, custom
  const [currentDate, setCurrentDate] = useState(new Date());
  const [monthData, setMonthData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load month data (covers day, week, month views)
  const loadMonth = useCallback(async (date) => {
    setLoading(true);
    try {
      const y = date.getFullYear();
      const m = date.getMonth() + 1;
      const res = await authFetch(`${API}/timesheet/month/${y}/${m}?user_id=${userId}`);
      if (res.ok) {
        const d = await res.json();
        setMonthData(d);
        setUserName(d.user_name || "");
      }
    } catch {}
    setLoading(false);
  }, [userId]);

  useEffect(() => { loadMonth(currentDate); }, [currentDate, loadMonth]);

  // Navigation
  const goPrev = () => {
    if (viewMode === "month") setCurrentDate(d => subMonths(d, 1));
    else setCurrentDate(d => new Date(d.getTime() - (viewMode === "week" ? 7 : 1) * 86400000));
  };
  const goNext = () => {
    if (viewMode === "month") setCurrentDate(d => addMonths(d, 1));
    else setCurrentDate(d => new Date(d.getTime() + (viewMode === "week" ? 7 : 1) * 86400000));
  };

  // Filter days for current view
  const filteredDays = useMemo(() => {
    if (!monthData?.days) return [];
    const days = monthData.days;
    const cd = format(currentDate, "yyyy-MM-dd");

    if (viewMode === "day") {
      return days.filter(d => d.date === cd);
    }
    if (viewMode === "week") {
      const ws = format(startOfWeek(currentDate, { weekStartsOn: 1 }), "yyyy-MM-dd");
      const we = format(endOfWeek(currentDate, { weekStartsOn: 1 }), "yyyy-MM-dd");
      return days.filter(d => d.date >= ws && d.date <= we);
    }
    return days; // month
  }, [monthData, viewMode, currentDate]);

  // Summary
  const summary = useMemo(() => {
    const work = filteredDays.reduce((s, d) => s + (d.total_work_minutes || 0), 0);
    const soll = filteredDays.reduce((s, d) => s + (d.soll_minutes || 0), 0);
    const diff = work - soll;
    const daysWorked = filteredDays.filter(d => d.has_entries).length;
    return { work, soll, diff, daysWorked };
  }, [filteredDays]);

  // Period label
  const periodLabel = useMemo(() => {
    if (viewMode === "day") return format(currentDate, "EEEE, dd. MMMM yyyy", { locale: de });
    if (viewMode === "week") {
      const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
      const we = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(ws, "dd.MM.")} – ${format(we, "dd.MM.yyyy")}`;
    }
    return format(currentDate, "MMMM yyyy", { locale: de });
  }, [viewMode, currentDate]);

  const diffColor = summary.diff >= 0 ? "text-emerald-400" : "text-red-400";
  const DiffIcon = summary.diff > 0 ? TrendingUp : summary.diff < 0 ? TrendingDown : Minus;

  return (
    <div className="min-h-screen bg-[#0f1117] text-white" data-testid="m-employee-stunden">
      {/* Header */}
      <div className="sticky top-0 z-20 px-4 py-3" style={{ background: "rgba(15,23,42,0.95)", backdropFilter: "blur(12px)" }}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10" data-testid="stunden-back">
            <ArrowLeft className="w-4 h-4 text-gray-400" />
          </button>
          <div className="min-w-0">
            <h1 className="text-sm font-bold text-white truncate">{userName || "Mitarbeiter"}</h1>
            <p className="text-[10px] text-gray-500">Stundenübersicht</p>
          </div>
        </div>
      </div>

      {/* View Mode Tabs */}
      <div className="px-4 pt-2 pb-1">
        <div className="flex bg-white/5 rounded-lg p-0.5" data-testid="stunden-view-tabs">
          {[
            { key: "day", label: "Tag" },
            { key: "week", label: "Woche" },
            { key: "month", label: "Monat" },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setViewMode(t.key)}
              className={`flex-1 py-2 rounded-md text-[11px] font-semibold transition-all ${viewMode === t.key ? "bg-blue-600 text-white shadow-md" : "text-gray-500"}`}
              data-testid={`stunden-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Period Navigation */}
      <div className="flex items-center justify-between px-4 py-2">
        <button onClick={goPrev} className="p-2 rounded-lg bg-white/5 hover:bg-white/10" data-testid="stunden-prev">
          <ChevronLeft className="w-4 h-4 text-gray-400" />
        </button>
        <span className="text-[12px] font-medium text-gray-300">{periodLabel}</span>
        <button onClick={goNext} className="p-2 rounded-lg bg-white/5 hover:bg-white/10" data-testid="stunden-next">
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 text-gray-500 animate-spin" /></div>
      ) : (
        <div className="px-3 pb-24 space-y-3">
          {/* Summary Card */}
          <div className="grid grid-cols-3 gap-2" data-testid="stunden-summary">
            <SummaryTile label="Ist" value={hm(summary.work)} icon={<Clock className="w-3.5 h-3.5" />} color="blue" />
            <SummaryTile label="Soll" value={hm(summary.soll)} icon={<Sun className="w-3.5 h-3.5" />} color="gray" />
            <SummaryTile
              label="Differenz"
              value={`${summary.diff >= 0 ? "+" : "-"}${hm(summary.diff)}`}
              icon={<DiffIcon className="w-3.5 h-3.5" />}
              color={summary.diff >= 0 ? "green" : "red"}
            />
          </div>

          {/* Days List */}
          {filteredDays.length === 0 ? (
            <div className="text-center py-12 text-gray-500 text-xs">Keine Daten fuer diesen Zeitraum</div>
          ) : (
            <div className="space-y-1.5">
              {filteredDays.map(day => (
                <DayRow key={day.date} day={day} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== SUMMARY TILE ====================

function SummaryTile({ label, value, icon, color }) {
  const colors = {
    blue: "from-blue-600/20 to-blue-600/5 border-blue-500/20 text-blue-400",
    green: "from-emerald-600/20 to-emerald-600/5 border-emerald-500/20 text-emerald-400",
    red: "from-red-600/20 to-red-600/5 border-red-500/20 text-red-400",
    gray: "from-gray-600/10 to-gray-600/5 border-gray-500/10 text-gray-400",
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-3`}>
      <div className="flex items-center gap-1 mb-1">
        {icon}
        <span className="text-[9px] uppercase tracking-wider text-gray-500">{label}</span>
      </div>
      <p className="text-lg font-bold font-mono tabular-nums">{value}</p>
    </div>
  );
}

// ==================== DAY ROW ====================

function DayRow({ day }) {
  const d = new Date(day.date + "T00:00:00");
  const isWeekend = day.is_weekend;
  const hasWork = day.total_work_minutes > 0;
  const isAbsence = day.absence_type && day.absence_type !== "arbeitstag";

  const absLabel = { urlaub: "Urlaub", krank: "Krank", feiertag: "Feiertag", frei: "Frei" };
  const absColor = { urlaub: "text-blue-400 bg-blue-400/10", krank: "text-red-400 bg-red-400/10", feiertag: "text-purple-400 bg-purple-400/10", frei: "text-gray-400 bg-gray-400/10" };

  const diff = day.differenz_minutes || 0;

  return (
    <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${isWeekend ? "bg-white/[0.02] opacity-50" : hasWork || isAbsence ? "bg-white/[0.04]" : "bg-white/[0.02] opacity-40"}`} data-testid={`day-${day.date}`}>
      {/* Date */}
      <div className="w-10 text-center shrink-0">
        <p className="text-[10px] text-gray-500">{format(d, "EE", { locale: de })}</p>
        <p className={`text-sm font-bold ${isWeekend ? "text-gray-600" : "text-white"}`}>{format(d, "dd")}</p>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {isAbsence ? (
          <span className={`text-[11px] font-medium px-2 py-0.5 rounded ${absColor[day.absence_type] || "text-gray-400 bg-gray-400/10"}`}>
            {absLabel[day.absence_type] || day.absence_type}
          </span>
        ) : hasWork ? (
          <div className="space-y-0.5">
            {(day.entries || []).slice(0, 2).map((e, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <Briefcase className="w-3 h-3 text-gray-600 shrink-0" />
                <span className="text-[11px] text-gray-400 truncate">{e.project_name || "–"}</span>
                <span className="text-[11px] font-mono text-gray-300 ml-auto shrink-0">{hm(e.work_time_minutes)}</span>
              </div>
            ))}
          </div>
        ) : (
          <span className="text-[11px] text-gray-600">{isWeekend ? "Wochenende" : "–"}</span>
        )}
      </div>

      {/* Totals */}
      <div className="text-right shrink-0 w-16">
        {hasWork && (
          <>
            <p className="text-[12px] font-bold font-mono text-white tabular-nums">{hm(day.total_work_minutes)}</p>
            {day.soll_minutes > 0 && (
              <p className={`text-[9px] font-mono tabular-nums ${diff >= 0 ? "text-emerald-500" : "text-red-400"}`}>
                {diff >= 0 ? "+" : ""}{hm(diff)}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
